/**
 * Done. CPD — OnboardingSurvey (Jo's 7-section schema)
 *
 * Multi-step survey navigator → per-section form → AI plan generation
 * → redirect to /dashboard.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { AILabel } from '@/components/ui/AILabel';
import { api, ApiError } from '@/lib/api';
import { SurveyOverview } from '@/components/survey/SurveyOverview';
import type { SurveySectionMeta, SurveyStatus } from '@/components/survey/SurveyOverview';
import type { SurveyData, AnnualPlan } from '@/lib/types';

interface OnboardingSurveyProps {
  onComplete?: () => void;
}

type View = 'overview' | 'section' | 'generating';
type SectionId = 'about' | 'practice' | 'confidence' | 'techniques' | 'learning' | 'direction' | 'scheduling';

// ── Question definitions ────────────────────────────────────────────────────

interface Question {
  id: keyof SurveyData;
  label: string;
  type: 'single' | 'multi' | 'textarea';
  options?: string[];
  optional?: boolean;
  placeholder?: string;
}

interface SectionDef {
  id: SectionId;
  title: string;
  description: string;
  questions: Question[];
  isComplete: (d: Partial<SurveyData>) => boolean;
}

const SECTIONS: SectionDef[] = [
  {
    id: 'about',
    title: 'About You',
    description: 'A few details help shape the tone and relevance of your annual plan.',
    questions: [
      { id: 'ageGroup',        label: 'Age group',          type: 'single', options: ['Under 25','25-34','35-44','45-54','55-64','65+'] },
      { id: 'gender',          label: 'Gender',              type: 'single', options: ['Male','Female','Non-binary / diverse','Prefer not to say'] },
      { id: 'yearsInPractice', label: 'Years in practice',   type: 'single', options: ['Student/New Graduate','0-2','3-5','6-10','11-20','21-30','30+'] },
      { id: 'role',            label: 'Role',                type: 'single', options: ['Associate','Contractor','Owner/operator','Multi-practice owner','Locum'] },
    ],
    isComplete: (d) => Boolean(d.ageGroup && d.gender && d.yearsInPractice && d.role),
  },
  {
    id: 'practice',
    title: 'Practice Environment',
    description: 'Your patient mix is central to CPD that feels useful and defensible.',
    questions: [
      { id: 'practiceLocation',    label: 'Practice location',               type: 'single', options: ['Major city','Regional town','Rural community'] },
      { id: 'patientBase',         label: 'Patient base',                    type: 'multi',  options: ['Office workers','Manual labourers','Farmers','Families','Athletes','Elderly patients','Pregnancy care','Infants/children','Neurological/complex cases','ACC/injury care','Wellness-focused patients'] },
      { id: 'ageGroupsSeen',       label: 'Age groups commonly seen',        type: 'multi',  options: ['Infants','Children','Teenagers','Adults','Elderly'] },
      { id: 'commonPresentations', label: 'Common presentations',            type: 'multi',  options: ['Lower back pain','Neck pain/headaches','Postural issues','Sports injuries','Extremities','Degenerative conditions','Balance/falls risk','Pregnancy-related complaints','Pediatric developmental concerns','Chronic pain','Occupational strain injuries'] },
    ],
    isComplete: (d) => Boolean(
      d.practiceLocation &&
      (d.patientBase?.length ?? 0) > 0 &&
      (d.ageGroupsSeen?.length ?? 0) > 0 &&
      (d.commonPresentations?.length ?? 0) > 0
    ),
  },
  {
    id: 'confidence',
    title: 'Clinical Confidence',
    description: 'Pick the areas where targeted learning would make practice feel easier.',
    questions: [
      { id: 'confidenceAreas',     label: 'Areas where you want more confidence', type: 'multi', options: ['Newborn examinations','Primitive reflexes','Pediatric assessment','Geriatric care','Balance and falls prevention','Extremity adjusting','Rehabilitation and exercises','Neurology','Imaging interpretation','Differential diagnosis','Chronic pain management','Technique refinement','Communication/reporting','Evidence-informed practice','Practice systems','Digital systems and AI','Staff leadership','Business management','Patient retention','Cultural competency','Ethics and professional boundaries'] },
      { id: 'weaknesses',          label: 'Current weaknesses or gaps in practice', type: 'textarea', optional: true, placeholder: 'For example: confidence with older patients, documentation, rehab progression...' },
      { id: 'demographicChanges',  label: 'Changes noticed in patient demographics or practice', type: 'textarea', optional: true, placeholder: 'For example: more elderly patients, more sedentary workers, more complex cases...' },
    ],
    isComplete: (d) => (d.confidenceAreas?.length ?? 0) > 0,
  },
  {
    id: 'techniques',
    title: 'Techniques & Philosophy',
    description: 'This helps balance technique, clinical reasoning, and professional identity.',
    questions: [
      { id: 'techniques',    label: 'Techniques regularly used', type: 'multi', options: ['Diversified','Gonstead','SOT','Activator/ABC','Blair Upper Cervical','Toggle Recoil','Syntropy','NET','NSA','Mixed approach'] },
      { id: 'deepenAreas',   label: 'Areas to deepen',           type: 'multi', options: ['Philosophy and vitalism','Evidence-informed practice','Neuroscience','Functional movement','Biomechanics','Communication','Technique mastery','Interdisciplinary care','Research literacy'] },
    ],
    isComplete: (d) => (d.techniques?.length ?? 0) > 0 && (d.deepenAreas?.length ?? 0) > 0,
  },
  {
    id: 'learning',
    title: 'Learning Style',
    description: 'Your plan should fit around real life, not fight it.',
    questions: [
      { id: 'preferredFormats', label: 'Preferred learning formats',  type: 'multi',  options: ['Short videos','Long-form lectures','Podcasts/audio','Reading articles','Research summaries','Worksheets','Case studies','Quizzes','Live webinars','Small peer groups'] },
      { id: 'dislikedFormats',  label: 'Disliked learning formats',   type: 'multi',  optional: true, options: ['Short videos','Long-form lectures','Podcasts/audio','Reading articles','Research summaries','Worksheets','Case studies','Quizzes','Live webinars','Small peer groups'] },
      { id: 'monthlyTime',      label: 'Monthly CPD time commitment', type: 'single', options: ['30 mins','1 hour','2 hours','3+ hours'] },
    ],
    isComplete: (d) => (d.preferredFormats?.length ?? 0) > 0 && Boolean(d.monthlyTime),
  },
  {
    id: 'direction',
    title: 'Professional Direction',
    description: 'These answers make the annual plan feel personally meaningful.',
    questions: [
      { id: 'outcomes',       label: 'Outcomes that matter most this year',          type: 'multi',    options: ['Better patient outcomes','More clinical confidence','Reduce stress/burnout','Improve communication','Improve technique precision','Grow a specific patient demographic','Stay current professionally','Improve business systems','Improve work/life balance','Prepare for later career sustainability'] },
      { id: 'fiveYearVision', label: 'Where would you like your practice to be in 5 years?', type: 'textarea', optional: true, placeholder: 'A short note is enough.' },
      { id: 'motivation',     label: 'Professional motivation',                      type: 'multi',    options: ['Helping patients','Clinical mastery','Philosophy and purpose','Business growth','Work/life balance','Leadership','Community contribution'] },
    ],
    isComplete: (d) => (d.outcomes?.length ?? 0) > 0 && (d.motivation?.length ?? 0) > 0,
  },
  {
    id: 'scheduling',
    title: 'CPD Scheduling',
    description: 'Choose when Done. CPD should place your monthly learning sessions.',
    questions: [
      { id: 'preferredDay',      label: 'Preferred CPD day',      type: 'single', options: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'] },
      { id: 'preferredTime',     label: 'Preferred CPD time',     type: 'single', options: ['Morning','Midday','Afternoon','Evening'] },
      { id: 'preferredCalendar', label: 'Preferred calendar',     type: 'single', options: ['Google Calendar','Apple Calendar','Outlook'] },
      { id: 'reminders',         label: 'Reminder preference',    type: 'multi',  options: ['Gentle reminders','Accountability prompts','Progress tracking','Peer discussion invitations'] },
    ],
    isComplete: (d) => Boolean(d.preferredDay && d.preferredTime && d.preferredCalendar),
  },
];

// ── Styles ──────────────────────────────────────────────────────────────────

const FIELD_LABEL    = 'stitch-font-label text-[12px] uppercase tracking-widest text-[var(--stitch-primary)] mb-2 block';
const FIELD_HINT     = 'stitch-font-body text-[13px] text-[var(--stitch-on-surface-variant)] mb-2';
const FIELD_INPUT    = 'w-full rounded-xl border border-[var(--stitch-outline-variant)] bg-white px-4 py-3 stitch-font-body text-[15px] text-[var(--stitch-on-surface)] placeholder:text-[var(--stitch-outline)] min-h-[48px] focus:outline-none focus:ring-2 focus:ring-[var(--stitch-primary)] focus:border-[var(--stitch-primary)] transition-colors';
const FIELD_TEXTAREA = `${FIELD_INPUT} resize-y leading-relaxed`;
const PRIMARY_BTN    = 'inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] stitch-font-label text-[12px] uppercase tracking-widest hover:bg-[var(--stitch-primary-container)] transition-colors stitch-ghost-shadow disabled:opacity-50';
const GHOST_BTN      = 'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-[var(--stitch-primary)] text-[var(--stitch-primary)] stitch-font-label text-[12px] uppercase tracking-widest hover:bg-[var(--stitch-primary)]/5 transition-colors';

// ── Main component ──────────────────────────────────────────────────────────

const SURVEY_DRAFT_KEY = 'done-cpd:survey-draft';

function loadDraft(): Partial<SurveyData> {
  try {
    const raw = localStorage.getItem(SURVEY_DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Partial<SurveyData>) : {};
  } catch {
    return {};
  }
}

function saveDraft(data: Partial<SurveyData>): void {
  try {
    localStorage.setItem(SURVEY_DRAFT_KEY, JSON.stringify(data));
  } catch { /* storage full — silent */ }
}

function clearDraft(): void {
  try { localStorage.removeItem(SURVEY_DRAFT_KEY); } catch { /* ignore */ }
}

export function OnboardingSurvey({ onComplete }: OnboardingSurveyProps): React.ReactElement {
  const [, navigate] = useLocation();
  const [view, setView] = useState<View>('overview');
  const [activeSection, setActiveSection] = useState<SectionId | null>(null);
  const [surveyData, setSurveyData] = useState<Partial<SurveyData>>(loadDraft);
  const [generating, setGenerating] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState<AnnualPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sectionHeadingRef = useRef<HTMLHeadingElement>(null);
  const overviewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (view === 'section') sectionHeadingRef.current?.focus();
    if (view === 'overview') overviewRef.current?.focus();
  }, [view, activeSection]);

  function update(patch: Partial<SurveyData>): void {
    setSurveyData(prev => {
      const next = { ...prev, ...patch };
      saveDraft(next);
      return next;
    });
  }

  const sections: SurveySectionMeta[] = useMemo(() => {
    const firstIncompleteId = SECTIONS.find(s => !s.isComplete(surveyData))?.id ?? null;
    return SECTIONS.map((s, i) => {
      let status: SurveyStatus;
      if (s.isComplete(surveyData)) status = 'complete';
      else if (s.id === firstIncompleteId) status = 'in_progress';
      else status = 'not_started';
      return { num: i + 1, id: s.id, title: s.title, description: s.description, duration: '~2 min', status, active: true };
    });
  }, [surveyData]);

  const canGenerate = useMemo(() => SECTIONS.every(s => s.isComplete(surveyData)), [surveyData]);

  const handleSelect = useCallback((id: string): void => {
    setActiveSection(id as SectionId);
    setView('section');
  }, []);

  const handleBackToOverview = useCallback((): void => {
    setView('overview');
    setActiveSection(null);
  }, []);

  const handleGenerate = useCallback(async (): Promise<void> => {
    setView('generating');
    setGenerating(true);
    setError(null);
    try {
      const plan = await api.post<AnnualPlan>('/api/ai/generate-plan', { surveyData });
      setGeneratedPlan(plan);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('AI generation limit reached. Your plan has been saved and you can edit it manually.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate plan. You can set up your plan manually.');
      }
    } finally {
      setGenerating(false);
    }
  }, [surveyData]);

  const handleFinish = useCallback((): void => {
    clearDraft();
    onComplete?.();
    navigate('/dashboard');
  }, [onComplete, navigate]);

  const handleSkip = useCallback(async (): Promise<void> => {
    try {
      await api.post('/api/auth/complete-survey', {});
    } catch { /* best-effort */ }
    clearDraft();
    onComplete?.();
    navigate('/dashboard');
  }, [onComplete, navigate]);

  if (view === 'overview') {
    return (
      <div ref={overviewRef} tabIndex={-1} className="outline-none">
        <SurveyOverview
          sections={sections}
          onSelect={handleSelect}
          onGenerate={() => { void handleGenerate(); }}
          canGenerate={canGenerate}
          onSkip={() => { void handleSkip(); }}
        />
      </div>
    );
  }

  if (view === 'generating') {
    return (
      <GenerateView
        generating={generating}
        plan={generatedPlan}
        error={error}
        onFinish={handleFinish}
        onBack={handleBackToOverview}
      />
    );
  }

  if (!activeSection) return <></>;

  return (
    <SectionForm
      sectionId={activeSection}
      data={surveyData}
      onUpdate={update}
      onBack={handleBackToOverview}
      headingRef={sectionHeadingRef}
    />
  );
}

// ── Section form ────────────────────────────────────────────────────────────

interface SectionFormProps {
  sectionId: SectionId;
  data: Partial<SurveyData>;
  onUpdate: (patch: Partial<SurveyData>) => void;
  onBack: () => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}

function SectionForm({ sectionId, data, onUpdate, onBack, headingRef }: SectionFormProps): React.ReactElement {
  const def = SECTIONS.find(s => s.id === sectionId);
  if (!def) return <></>;

  return (
    <div className="flex flex-col gap-8">
      <div>
        <button
          type="button"
          onClick={onBack}
          className="stitch-font-label text-[12px] uppercase tracking-widest text-[var(--stitch-on-surface-variant)] hover:text-[var(--stitch-primary)] inline-flex items-center gap-1 mb-6"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Survey overview
        </button>
        <p className="stitch-font-label text-[12px] uppercase tracking-widest text-[var(--stitch-on-surface-variant)] mb-2">
          Section {String(SECTIONS.findIndex(s => s.id === sectionId) + 1).padStart(2, '0')}
        </p>
        <h2
          ref={headingRef}
          tabIndex={-1}
          className="stitch-font-display text-[36px] md:text-[44px] text-[var(--stitch-primary)] uppercase tracking-tight leading-[1.05] outline-none"
        >
          {def.title}
        </h2>
        <p className="stitch-font-body text-[16px] text-[var(--stitch-on-surface-variant)] mt-4 max-w-2xl leading-relaxed">
          {def.description}
        </p>
      </div>

      <div className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-2xl p-6 md:p-8 stitch-editorial-shadow">
        <div className="flex flex-col gap-6">
          {def.questions.map(q => (
            <QuestionField
              key={q.id}
              question={q}
              data={data}
              onUpdate={onUpdate}
            />
          ))}
          <SectionFooter
            onBack={onBack}
            onSave={onBack}
            canSave={def.isComplete(data)}
          />
        </div>
      </div>
    </div>
  );
}

function QuestionField({
  question: q,
  data,
  onUpdate,
}: {
  question: Question;
  data: Partial<SurveyData>;
  onUpdate: (patch: Partial<SurveyData>) => void;
}): React.ReactElement {
  const value = data[q.id];

  if (q.type === 'single') {
    const current = typeof value === 'string' ? value : '';
    return (
      <fieldset>
        <legend className={FIELD_LABEL}>
          {q.label}
          {q.optional && <span className="ml-1 normal-case text-[var(--stitch-outline)]">(optional)</span>}
        </legend>
        <div className="flex flex-wrap gap-2 mt-1">
          {(q.options ?? []).map(opt => {
            const active = current === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => { onUpdate({ [q.id]: opt } as Partial<SurveyData>); }}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-[14px] stitch-font-body border transition-colors ${
                  active
                    ? 'bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] border-[var(--stitch-primary)]'
                    : 'bg-white text-[var(--stitch-on-surface-variant)] border-[var(--stitch-outline-variant)] hover:border-[var(--stitch-primary)]'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (q.type === 'multi') {
    const current: string[] = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (opt: string): void => {
      const next = current.includes(opt) ? current.filter(s => s !== opt) : [...current, opt];
      onUpdate({ [q.id]: next } as Partial<SurveyData>);
    };
    return (
      <fieldset>
        <legend className={FIELD_LABEL}>
          {q.label}
          {q.optional && <span className="ml-1 normal-case text-[var(--stitch-outline)]">(optional)</span>}
        </legend>
        <div className="flex flex-wrap gap-2 mt-1">
          {(q.options ?? []).map(opt => {
            const active = current.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => { toggle(opt); }}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-[14px] stitch-font-body border transition-colors ${
                  active
                    ? 'bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)] border-[var(--stitch-primary)]'
                    : 'bg-white text-[var(--stitch-on-surface-variant)] border-[var(--stitch-outline-variant)] hover:border-[var(--stitch-primary)]'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </fieldset>
    );
  }

  if (q.type === 'textarea') {
    const current = typeof value === 'string' ? value : '';
    return (
      <div>
        <label htmlFor={q.id} className={FIELD_LABEL}>
          {q.label}
          {q.optional && <span className="ml-1 normal-case text-[var(--stitch-outline)]">(optional)</span>}
        </label>
        {q.placeholder && <p className={FIELD_HINT}>{q.placeholder}</p>}
        <textarea
          id={q.id}
          rows={3}
          value={current}
          onChange={e => { onUpdate({ [q.id]: e.target.value } as Partial<SurveyData>); }}
          className={FIELD_TEXTAREA}
        />
      </div>
    );
  }

  return <></>;
}

function SectionFooter({
  onBack,
  onSave,
  canSave,
}: {
  onBack: () => void;
  onSave: () => void;
  canSave: boolean;
}): React.ReactElement {
  return (
    <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 border-t border-[var(--stitch-outline-variant)]">
      <button type="button" onClick={onBack} className={GHOST_BTN}>
        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
        Back
      </button>
      <button
        type="button"
        onClick={onSave}
        aria-disabled={!canSave || undefined}
        className={`${PRIMARY_BTN} ${canSave ? '' : 'opacity-50 cursor-not-allowed'}`}
      >
        Save section
        <span className="material-symbols-outlined text-[16px]">check</span>
      </button>
    </div>
  );
}

// ── Generate view ───────────────────────────────────────────────────────────

interface GenerateViewProps {
  generating: boolean;
  plan: AnnualPlan | null;
  error: string | null;
  onFinish: () => void;
  onBack: () => void;
}

function GenerateView({ generating, plan, error, onFinish, onBack }: GenerateViewProps): React.ReactElement {
  if (generating) {
    return (
      <div className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-3xl p-12 text-center stitch-editorial-shadow flex flex-col items-center gap-6">
        <span className="material-symbols-outlined text-[64px] text-[var(--stitch-primary)] animate-spin">progress_activity</span>
        <div>
          <h2 className="stitch-font-display text-[28px] md:text-[32px] uppercase text-[var(--stitch-primary)] tracking-tight">
            Drafting your CPD plan
          </h2>
          <p className="stitch-font-body text-[15px] text-[var(--stitch-on-surface-variant)] mt-2">
            This usually takes 10–15 seconds.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-3xl p-10 stitch-editorial-shadow flex flex-col gap-6">
        <div>
          <h2 className="stitch-font-display text-[28px] uppercase text-[var(--stitch-primary)] tracking-tight">
            Plan generation paused
          </h2>
          <p className="stitch-font-body text-[15px] text-[var(--stitch-on-surface-variant)] mt-3 leading-relaxed">{error}</p>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
          <button type="button" onClick={onBack} className={GHOST_BTN}>Back to overview</button>
          <button type="button" onClick={onFinish} className={PRIMARY_BTN}>
            Continue to dashboard
            <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
          </button>
        </div>
      </div>
    );
  }

  if (plan) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <p className="stitch-font-label text-[12px] uppercase tracking-widest text-[var(--stitch-on-surface-variant)] mb-2">
            Your personalised plan
          </p>
          <h2 className="stitch-font-display text-[36px] md:text-[44px] text-[var(--stitch-primary)] uppercase tracking-tight leading-[1.05]">
            Annual CPD plan
          </h2>
        </div>
        <article className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-3xl p-8 md:p-10 stitch-editorial-shadow">
          <div className="flex items-center justify-between mb-5">
            <h3 className="stitch-font-headline text-[18px] uppercase text-[var(--stitch-primary)]">Draft outline</h3>
            <AILabel />
          </div>
          <div className="stitch-font-body text-[15px] text-[var(--stitch-on-surface)] whitespace-pre-wrap leading-relaxed max-h-[420px] overflow-y-auto">
            {plan.planText}
          </div>
          <p className="stitch-font-body text-[13px] text-[var(--stitch-on-surface-variant)] mt-5 italic">
            Review and edit this plan at any time from your dashboard.
          </p>
        </article>
        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
          <button type="button" onClick={onBack} className={GHOST_BTN}>Back to overview</button>
          <button type="button" onClick={onFinish} className={PRIMARY_BTN}>
            Go to dashboard
            <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
          </button>
        </div>
      </div>
    );
  }

  return <div />;
}
